import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestJson, requestMultipart, requestVoid } from '../../lib/util';
import { usePxtraCardEnabled } from '../../hooks/usePxtraCardEnabled';
import {
  CardOnboardingDocumentCollection,
  CardOnboardingAccountAdmin,
  CardOnboardingRepresentation,
  CardOnboardingResponse,
  MonthlyPaymentVolume
} from './types';
import { shouldRetryCardQuery } from './walletError';

/**
 * The legacy backend forwards these to the new backend, which owns the KYB
 * and talks to Swan.
 */
const onboardingPath = (companyId: string) =>
  `/api/v2/companies/${companyId}/pxtra-card/onboarding`;

export const cardOnboardingKey = (companyId: string) => ['cardOnboarding', companyId];

/** Matches the backend's own refresh throttle, so polling costs it nothing. */
const STATUS_POLL_MS = 60_000;

/** Nothing moves by itself once Swan has decided. */
const isOnboardingSettled = (onboarding?: CardOnboardingResponse) =>
  onboarding?.phase === 'verified' || onboarding?.phase === 'refused';

export const useCardOnboarding = (companyId: string, options = {}) => {
  const { isCardEnabled } = usePxtraCardEnabled();

  return useQuery<CardOnboardingResponse>({
    queryKey: cardOnboardingKey(companyId),
    queryFn: () => requestJson<CardOnboardingResponse>({ path: onboardingPath(companyId) }),
    enabled: !!companyId && isCardEnabled,
    // Reading the stand costs a Swan round trip. The appbar mounts this on
    // every page, so navigating within the dashboard is no reason to spend
    // one, and the backend throttles to one call a minute anyway.
    staleTime: STATUS_POLL_MS,
    retry: shouldRetryCardQuery,
    // While the setup is running its state changes behind the manager's back:
    // Swan finishes a review, the signature lands, a document is refused. The
    // page has to notice that without being reloaded by hand — and once the
    // company is verified there is nothing left to watch.
    refetchInterval: (query) => (isOnboardingSettled(query.state.data) ? false : STATUS_POLL_MS),
    refetchOnWindowFocus: (query) => !isOnboardingSettled(query.state.data),
    ...options
  });
};

export const useOnboardingDocuments = (companyId: string, enabled: boolean) =>
  useQuery<CardOnboardingDocumentCollection>({
    queryKey: [...cardOnboardingKey(companyId), 'documents'],
    queryFn: () =>
      requestJson<CardOnboardingDocumentCollection>({
        path: `${onboardingPath(companyId)}/documents`
      }),
    enabled: enabled && !!companyId
  });

export const useStartCardOnboarding = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      representation,
      accountAdmin
    }: {
      representation: CardOnboardingRepresentation;
      accountAdmin: CardOnboardingAccountAdmin;
    }) =>
      requestJson<{ onboarding_url: string }>({
        path: onboardingPath(companyId),
        method: 'POST',
        body: { representation, account_admin: accountAdmin }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardOnboardingKey(companyId) });
    }
  });
};

export const useUploadOnboardingDocument = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      purpose,
      documentType,
      file
    }: {
      purpose: string;
      documentType: string;
      file: File;
    }) => {
      const form = new FormData();
      form.append('purpose', purpose);
      form.append('document_type', documentType);
      form.append('file', file);

      return requestMultipart({
        path: `${onboardingPath(companyId)}/documents`,
        form
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardOnboardingKey(companyId) });
    }
  });
};

export const useSubmitOnboardingDocuments = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => requestVoid({ path: `${onboardingPath(companyId)}/documents/review` }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardOnboardingKey(companyId) });
    }
  });
};

export const useSetOnboardingTaxId = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subjectId,
      taxIdentificationNumber
    }: {
      subjectId: string;
      taxIdentificationNumber: string;
    }) =>
      requestVoid({
        path: `${onboardingPath(companyId)}/tax-id`,
        body: {
          subject_id: subjectId,
          tax_identification_number: taxIdentificationNumber
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardOnboardingKey(companyId) });
    }
  });
};

export const useSetMonthlyVolume = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (monthlyPaymentVolume: MonthlyPaymentVolume) =>
      requestVoid({
        path: `${onboardingPath(companyId)}/monthly-volume`,
        method: 'PATCH',
        body: { monthly_payment_volume: monthlyPaymentVolume }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardOnboardingKey(companyId) });
    }
  });
};

/**
 * Swan's identification link. Fetched on demand rather than with the page:
 * it carries a signed state with a short life, so it is minted when the
 * representative is actually about to use it.
 */
export const useCardIdentificationUrl = (companyId: string) =>
  useMutation({
    mutationFn: () =>
      requestJson<{ identification_url: string }>({
        path: `${onboardingPath(companyId)}/identification`
      })
  });
